import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import {Stock} from "../components/model/stock";
import {StockInOut} from "../components/model/stock-in-out";
import { StockResponse, StockHistoryResponse, StockLot, StockLotFilters, StockLotResponse } from '../interfaces/stock.interface';

const lotFilterParams = (filters: StockLotFilters): string => {
    const params = new URLSearchParams();
    if (filters.storeId) params.set('storeId', filters.storeId);
    if (filters.itemId) params.set('itemId', filters.itemId);
    if (filters.shade) params.set('shade', filters.shade);
    if (filters.dyeLot) params.set('dyeLot', filters.dyeLot);
    if (filters.grade) params.set('grade', filters.grade);
    if (filters.onlyAvailable) params.set('onlyAvailable', 'true');
    if (filters.search) params.set('search', filters.search);
    return params.toString();
};

export const StockService = {
    /** Sets (or clears, when `value` is null) the reorder level of one stock row. */
    setReorderLevel: async (stockId: string, value: number | null): Promise<Stock> => {
        const response = await axios.patch(ENDPOINTS.STOCK.REORDER_LEVEL(stockId), null, {
            params: value === null ? {} : { value },
        });
        return response.data;
    },

    stockIn: async (stock: StockInOut): Promise<Stock> => {
        const response = await axios.patch(ENDPOINTS.STOCK.STOCK_IN, stock);
        return response.data;
    },

    stockOut: async (stock: StockInOut): Promise<Stock> => {
        const response = await axios.patch(ENDPOINTS.STOCK.STOCK_OUT, stock);
        return response.data;
    },

    getStockByStoreAndItem: async (storeId: string, itemId: string): Promise<Stock> => {
        const response = await axios.get(`${ENDPOINTS.STOCK.STOCK_BY_STORE_AND_ITEM(storeId, itemId)}`);
        return response.data;
    },

    getStockByStore: async (storeId: string, page: number = 0, size: number = 10, search: string = ''): Promise<StockResponse> => {
        const response = await axios.get(`${ENDPOINTS.STOCK.STOCK_BY_STORE(storeId)}?page=${page}&size=${size}&search=${encodeURIComponent(search)}`);
        return response.data;
    },

    /** Stock of every store the caller may see (a store in-charge sees only their own). */
    getStockByInstitution: async (institutionId: string, page: number = 0, size: number = 10, search: string = ''): Promise<StockResponse> => {
        const response = await axios.get(`${ENDPOINTS.STOCK.STOCK_BY_INSTITUTION(institutionId)}?page=${page}&size=${size}&search=${encodeURIComponent(search)}`);
        return response.data;
    },

    getStockHistory: async (storeId: string, itemId: string, page: number = 0, size: number = 10): Promise<StockHistoryResponse> => {
        const response = await axios.get(`${ENDPOINTS.STOCK.STOCK_HISTORY(storeId, itemId)}?page=${page}&size=${size}`);
        return response.data;
    },

    /** Whole-institution history, paginated; store and item are optional server-side filters. */
    searchStockHistory: async (
        institutionId: string,
        filters: { storeId?: string; itemId?: string },
        page: number = 0,
        size: number = 10,
    ): Promise<StockHistoryResponse> => {
        const params = new URLSearchParams({ page: String(page), size: String(size) });
        if (filters.storeId) params.set('storeId', filters.storeId);
        if (filters.itemId) params.set('itemId', filters.itemId);
        const response = await axios.get(`${ENDPOINTS.STOCK.HISTORY_SEARCH(institutionId)}?${params.toString()}`);
        return response.data;
    },

    getLots: async (storeId: string, itemId: string): Promise<StockLot[]> => {
        const response = await axios.get(`${ENDPOINTS.STOCK.LOTS(storeId, itemId)}`);
        return response.data;
    },

    getLowStock: async (institutionId: string): Promise<Stock[]> => {
        const response = await axios.get(ENDPOINTS.STOCK.LOW_STOCK(institutionId));
        return response.data;
    },

    searchLots: async (
        institutionId: string, filters: StockLotFilters, page: number = 0, size: number = 20
    ): Promise<StockLotResponse> => {
        const query = lotFilterParams(filters);
        const response = await axios.get(
            `${ENDPOINTS.STOCK.LOT_SEARCH(institutionId)}?${query}&page=${page}&size=${size}`
        );
        return response.data;
    },

    downloadLotRegisterXlsx: async (institutionId: string, filters: StockLotFilters): Promise<Blob> => {
        const query = lotFilterParams(filters);
        const response = await axios.get(
            `${ENDPOINTS.STOCK.LOT_REGISTER_XLSX(institutionId)}?${query}`,
            { responseType: 'blob' }
        );
        return response.data;
    },

    getAvailableLots: async (
        storeId: string, itemId: string, shade?: string, dyeLot?: string
    ): Promise<StockLot[]> => {
        const params = new URLSearchParams();
        if (shade) params.set('shade', shade);
        if (dyeLot) params.set('dyeLot', dyeLot);
        const query = params.toString();
        const response = await axios.get(
            `${ENDPOINTS.STOCK.AVAILABLE_LOTS(storeId, itemId)}${query ? `?${query}` : ''}`
        );
        return response.data;
    },
};
